<template>
    <button
        :type="type"
        :disabled="disabled || loading"
        :class="classes"
        :data-testid="'bmv-btn'"
        @click="$emit('click', $event)"
    >
        <span v-if="loading" class="bmv-btn-spinner" aria-hidden="true"></span>
        <i v-else-if="icon" :class="icon" class="bmv-btn-icon"></i>
        <span v-if="$slots.default" class="bmv-btn-label"><slot /></span>
        <i v-if="iconRight && !loading" :class="iconRight" class="bmv-btn-icon bmv-btn-icon--right"></i>
    </button>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
const VARIANTS = ['primary', 'secondary', 'ghost', 'danger', 'success', 'neutral', 'warning']
const SIZES = ['sm', 'md', 'lg']

export default {
    name: 'BmvButton',
    props: {
        variant: {
            type: String,
            default: 'primary',
            validator: (v) => VARIANTS.includes(v)
        },
        size: {
            type: String,
            default: 'md',
            validator: (v) => SIZES.includes(v)
        },
        type: {
            type: String,
            default: 'button'
        },
        icon: { type: String, default: '' },
        iconRight: { type: String, default: '' },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        block: { type: Boolean, default: false }
    },
    computed: {
        classes() {
            return [
                'bmv-btn',
                `bmv-btn--${this.variant}`,
                `bmv-btn--${this.size}`,
                {
                    'bmv-btn--block': this.block,
                    'bmv-btn--loading': this.loading,
                    'bmv-btn--icon-only': !this.$slots.default && (this.icon || this.iconRight)
                }
            ]
        }
    }
}
</script>

<style scoped>
.bmv-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-2);
    border: 1px solid transparent !important;
    border-radius: var(--v2-radius-md);
    font-family: var(--v2-font-sans);
    font-weight: var(--v2-font-weight-medium);
    cursor: pointer;
    transition: background-color var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast);
    white-space: nowrap;
    user-select: none;
    line-height: 1;
    box-sizing: border-box !important;
    outline: none;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    margin: 0;
    text-decoration: none;
    /* Force a GPU compositing layer to suppress sub-pixel rendering jitter
     * some browsers apply between :hover / :active on plain <button> elements. */
    transform: translateZ(0);
    backface-visibility: hidden;
    font-feature-settings: 'kern';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}

.bmv-btn,
.bmv-btn:hover,
.bmv-btn:focus,
.bmv-btn:focus-visible,
.bmv-btn:active {
    outline: none !important;
    text-indent: 0 !important;
    transform: translateZ(0) !important;
    -webkit-tap-highlight-color: transparent !important;
}

.bmv-btn::-moz-focus-inner {
    border: 0;
    padding: 0;
}

.bmv-btn:focus-visible {
    box-shadow: var(--v2-shadow-focus);
}

.bmv-btn:disabled,
.bmv-btn--loading {
    cursor: not-allowed;
    opacity: 0.6;
}

/* Sizes */
.bmv-btn--sm {
    height: 28px;
    padding: 0 var(--v2-space-3);
    font-size: var(--v2-text-xs-size);
}

.bmv-btn--md {
    height: 34px;
    padding: 0 var(--v2-space-4);
    font-size: var(--v2-text-sm-size);
}

.bmv-btn--lg {
    height: 40px;
    padding: 0 var(--v2-space-5);
    font-size: var(--v2-text-base-size);
}

.bmv-btn--icon-only.bmv-btn--sm { width: 28px; padding: 0; }
.bmv-btn--icon-only.bmv-btn--md { width: 34px; padding: 0; }
.bmv-btn--icon-only.bmv-btn--lg { width: 40px; padding: 0; }

.bmv-btn--block {
    width: 100%;
}

/* Variants */
.bmv-btn--primary {
    background: var(--v2-primary);
    color: var(--v2-primary-contrast);
    border-color: var(--v2-primary);
}

.bmv-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover);
    border-color: var(--v2-primary-hover);
}

.bmv-btn--secondary {
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    border-color: var(--v2-border);
}

.bmv-btn--secondary:hover:not(:disabled) {
    background: var(--v2-surface-1);
    border-color: var(--v2-border-strong);
}

.bmv-btn--ghost {
    background: transparent;
    color: var(--v2-text-2);
    border-color: transparent;
}

.bmv-btn--ghost:hover:not(:disabled) {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.bmv-btn--neutral {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
    border-color: transparent;
}

.bmv-btn--neutral:hover:not(:disabled) {
    background: var(--v2-surface-3);
}

.bmv-btn--danger {
    background: var(--v2-danger);
    color: #ffffff;
    border-color: var(--v2-danger);
}

.bmv-btn--danger:hover:not(:disabled) {
    filter: brightness(0.95);
}

.bmv-btn--success {
    background: var(--v2-success);
    color: #ffffff;
    border-color: var(--v2-success);
}

.bmv-btn--success:hover:not(:disabled) {
    filter: brightness(0.95);
}

.bmv-btn--warning {
    background: #f59e0b;
    color: #ffffff;
    border-color: #f59e0b;
}

.bmv-btn--warning:hover:not(:disabled) {
    filter: brightness(0.95);
}

/* Icons */
.bmv-btn-icon {
    font-size: 0.95em;
    color: currentColor;
    line-height: 1;
}

.bmv-btn-icon--right {
    margin-left: calc(var(--v2-space-1) * -1);
}

.bmv-btn-label {
    line-height: 1;
}

/* Loading spinner */
.bmv-btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: bmv-btn-spin 0.7s linear infinite;
}

@keyframes bmv-btn-spin {
    to { transform: rotate(360deg); }
}
</style>
